import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { CoachingIntervalType } from '../../models/coaching-interval.model';
import { ColorService } from '../../services/color-service';

type CoachingIntervalSegmentType = {
  interval: CoachingIntervalType;
  duration: number;
  widthPx: number;
  elapsedPercent: number;
};

@Component({
  selector: 'app-intervals-timeline',
  templateUrl: './intervals-timeline.html',
  styleUrl: './intervals-timeline.scss',
  host: {
    '[class.centered]': 'centered()',
  },
})
export class IntervalsTimeline {
  private static readonly CENTERED_TIMELINE_SCALE: number = 6;
  private static readonly CENTERED_PLAYHEAD_PERCENT: number = 50;
  private static readonly SEGMENT_MIN_WIDTH_REM: number = 2.5;
  private static readonly SEGMENT_GAP_REM: number = 0.5;

  protected readonly colorService: ColorService = inject(ColorService);
  private readonly hostElement: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly hostWidthPx: WritableSignal<number> = signal(0);

  intervals: InputSignal<CoachingIntervalType[]> = input.required<CoachingIntervalType[]>();
  currentTime: InputSignal<number> = input.required<number>();
  totalDuration: InputSignal<number> = input.required<number>();
  centered: InputSignal<boolean> = input<boolean>(false);

  constructor() {
    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver: ResizeObserver = new ResizeObserver(([entry]: ResizeObserverEntry[]) => {
      this.hostWidthPx.set(entry.contentRect.width);
    });
    resizeObserver.observe(this.hostElement.nativeElement);
    this.destroyRef.onDestroy(() => resizeObserver.disconnect());
  }

  protected readonly intervalSegments: Signal<CoachingIntervalSegmentType[]> = computed(() => {
    const intervals: CoachingIntervalType[] = this.intervals();
    const totalDuration: number = this.totalDuration();
    const currentTime: number = this.currentTime();
    const baseTrackWidthPx: number = this.hostWidthPxValue() * this.timelineScale();
    const minSegmentWidthPx: number =
      IntervalsTimeline.SEGMENT_MIN_WIDTH_REM * this.rootFontSizePx();

    return intervals
      .map((interval: CoachingIntervalType) => {
        const duration: number = Math.max(interval.duration, 0);
        const durationPercent: number = totalDuration === 0 ? 0 : duration / totalDuration;
        const elapsedPercent: number =
          duration === 0
            ? 0
            : Math.min(Math.max(((currentTime - interval.position) / duration) * 100, 0), 100);
        return {
          interval,
          duration,
          widthPx: Math.max(durationPercent * baseTrackWidthPx, minSegmentWidthPx),
          elapsedPercent,
        };
      })
      .filter((segment: CoachingIntervalSegmentType) => segment.duration > 0);
  });

  private readonly timelineTrackPlayheadLeftPx: Signal<number> = computed(() => {
    const currentTime: number = this.currentTime();
    const segments: CoachingIntervalSegmentType[] = this.intervalSegments();
    const gapPx: number = this.segmentGapPx();
    let elapsedBeforeCurrentSegmentPx: number = 0;

    for (let index: number = 0; index < segments.length; index++) {
      const segment: CoachingIntervalSegmentType = segments[index];
      const segmentStart: number = segment.interval.position;
      const segmentEnd: number = segmentStart + segment.duration;

      if (currentTime >= segmentEnd) {
        elapsedBeforeCurrentSegmentPx += segment.widthPx + gapPx;
        continue;
      }

      const segmentProgress: number = Math.min(
        Math.max((currentTime - segmentStart) / segment.duration, 0),
        1,
      );

      return elapsedBeforeCurrentSegmentPx + segment.widthPx * segmentProgress;
    }

    return this.timelineTrackWidthPx();
  });

  protected readonly timelinePlayheadLeft: Signal<string> = computed(() => {
    return `${this.timelineTrackLeftPx() + this.timelineTrackPlayheadLeftPx()}px`;
  });

  protected readonly timelineTrackWidth: Signal<string> = computed(
    () => `${this.timelineTrackWidthPx()}px`,
  );

  protected readonly timelineTrackLeft: Signal<string> = computed(() => {
    return `${this.timelineTrackLeftPx()}px`;
  });

  private timelineScale(): number {
    return this.centered() ? IntervalsTimeline.CENTERED_TIMELINE_SCALE : 1;
  }

  private timelineTrackLeftPx(): number {
    const hostWidthPx: number = this.hostWidthPxValue();
    const trackWidthPx: number = this.timelineTrackWidthPx();
    if (hostWidthPx === 0 || trackWidthPx <= hostWidthPx) return 0;

    const minLeftPx: number = hostWidthPx - trackWidthPx;
    const playheadAnchorPx: number =
      hostWidthPx * (IntervalsTimeline.CENTERED_PLAYHEAD_PERCENT / 100);
    const centeredLeftPx: number = playheadAnchorPx - this.timelineTrackPlayheadLeftPx();

    return Math.min(Math.max(centeredLeftPx, minLeftPx), 0);
  }

  private timelineTrackWidthPx(): number {
    const segments: CoachingIntervalSegmentType[] = this.intervalSegments();
    const segmentsWidthPx: number = segments.reduce(
      (total: number, segment: CoachingIntervalSegmentType) => total + segment.widthPx,
      0,
    );
    const gapsWidthPx: number = Math.max(segments.length - 1, 0) * this.segmentGapPx();
    return segmentsWidthPx + gapsWidthPx;
  }

  private segmentGapPx(): number {
    return IntervalsTimeline.SEGMENT_GAP_REM * this.rootFontSizePx();
  }

  private rootFontSizePx(): number {
    if (typeof document === 'undefined') return 16;
    return Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  }

  private hostWidthPxValue(): number {
    return this.hostWidthPx() || this.hostElement.nativeElement.clientWidth || 0;
  }
}
